import { useState, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * VisualContextEngine Component
 * Generates relevant diagrams or concept sketches based on lesson content
 */
export function VisualContextEngine({
  lessonContent,
  gradeLevel,
  subject,
  persona
}) {
  const [diagrams, setDiagrams] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState("");
  const [selectedDiagram, setSelectedDiagram] = useState(null);
  
  // Generate a concept diagram based on topic
  const generateDiagram = useCallback(async (topic) => {
    setIsGenerating(true);
    setError("");
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/classroom/generate-diagram`,
        {
          topic,
          grade_level: gradeLevel,
          subject,
          context: lessonContent?.substring(0, 500) || ""
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.diagram_url) {
        setDiagrams(prev => [...prev, {
          id: Date.now(),
          topic,
          url: response.data.diagram_url,
          description: response.data.description
        }]);
      }
    } catch (err) {
      console.error("Diagram generation error:", err);
      setError("Could not generate diagram. Try again.");
    } finally {
      setIsGenerating(false);
    }
  }, [gradeLevel, subject, lessonContent]);
  
  // Extract key concepts from lesson content for diagram suggestions
  const suggestDiagrams = useCallback(() => {
    if (!lessonContent) return [];
    
    // Simple keyword extraction for diagram suggestions
    const keywords = [
      // Math concepts
      { pattern: /fraction|divide|parts/i, suggestion: "Fractions Visual" },
      { pattern: /geometry|shape|angle|triangle|circle/i, suggestion: "Geometry Shapes" },
      { pattern: /graph|chart|data/i, suggestion: "Data Visualization" },
      { pattern: /equation|formula|algebra/i, suggestion: "Equation Diagram" },
      
      // Science concepts
      { pattern: /cell|organism|biology/i, suggestion: "Cell Structure" },
      { pattern: /solar system|planet|space/i, suggestion: "Solar System Map" },
      { pattern: /atom|molecule|chemistry/i, suggestion: "Atomic Structure" },
      { pattern: /ecosystem|food chain/i, suggestion: "Food Chain Diagram" },
      
      // History/Social Studies
      { pattern: /timeline|history|era|period/i, suggestion: "Historical Timeline" },
      { pattern: /map|geography|region|country/i, suggestion: "Geographic Map" },
      { pattern: /government|branches|constitution/i, suggestion: "Government Structure" },
      
      // Biblical/Yisrael concepts
      { pattern: /tribe|twelve tribes|yisrael/i, suggestion: "12 Tribes of Yisrael" },
      { pattern: /tabernacle|temple|sanctuary/i, suggestion: "Tabernacle Layout" },
      { pattern: /exodus|journey|wilderness/i, suggestion: "Exodus Route Map" },
      { pattern: /feast|sabbath|holy day/i, suggestion: "Biblical Feast Calendar" },
      { pattern: /covenant|promise|abraham/i, suggestion: "Covenant Timeline" }
    ];
    
    const suggestions = [];
    for (const { pattern, suggestion } of keywords) {
      if (pattern.test(lessonContent)) {
        suggestions.push(suggestion);
      }
    }
    
    return suggestions.slice(0, 4); // Max 4 suggestions
  }, [lessonContent]);
  
  const suggestions = suggestDiagrams();
  
  return (
    <div style={{ marginTop: "20px" }}>
      {/* Header */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: "15px"
      }}>
        <h4 style={{ 
          margin: 0, 
          color: persona?.color || "#FFD700",
          display: "flex",
          alignItems: "center",
          gap: "8px"
        }}>
          📊 Visual Learning
        </h4>
        {isGenerating && (
          <span style={{ color: "#aaa", fontSize: "0.8rem" }}>
            Generating...
          </span>
        )}
      </div>
      
      {/* Diagram Suggestions */}
      {suggestions.length > 0 && (
        <div style={{ marginBottom: "15px" }}>
          <p style={{ color: "#aaa", fontSize: "0.85rem", marginBottom: "8px" }}>
            Generate a diagram:
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            {suggestions.map((suggestion, idx) => (
              <button
                key={idx}
                onClick={() => generateDiagram(suggestion)}
                disabled={isGenerating}
                style={{
                  background: "#222",
                  color: persona?.color || "#FFD700",
                  border: `1px solid ${persona?.color || "#FFD700"}`,
                  borderRadius: "15px",
                  padding: "6px 12px",
                  fontSize: "0.8rem",
                  cursor: isGenerating ? "not-allowed" : "pointer",
                  opacity: isGenerating ? 0.5 : 1
                }}
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      )}
      
      {/* Error Message */}
      {error && (
        <div style={{
          background: "#4d1a1a",
          color: "#ff6b6b",
          padding: "10px",
          borderRadius: "8px",
          marginBottom: "15px",
          fontSize: "0.85rem"
        }}>
          {error}
        </div>
      )}
      
      {/* Generated Diagrams */}
      {diagrams.length > 0 && (
        <div>
          <p style={{ color: "#aaa", fontSize: "0.85rem", marginBottom: "10px" }}>
            Generated Visuals:
          </p>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "10px"
          }}>
            {diagrams.map((diagram) => (
              <div
                key={diagram.id}
                onClick={() => setSelectedDiagram(diagram)}
                style={{
                  background: "#1a1a2a",
                  borderRadius: "10px",
                  overflow: "hidden",
                  cursor: "pointer",
                  border: selectedDiagram?.id === diagram.id 
                    ? `2px solid ${persona?.color || "#FFD700"}`
                    : "2px solid transparent"
                }}
              >
                <img
                  src={diagram.url}
                  alt={diagram.topic}
                  style={{
                    width: "100%",
                    height: "100px",
                    objectFit: "cover"
                  }}
                />
                <div style={{
                  padding: "8px",
                  color: "#ddd",
                  fontSize: "0.75rem"
                }}>
                  {diagram.topic}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Placeholder when no diagrams */}
      {diagrams.length === 0 && suggestions.length === 0 && (
        <div style={{
          background: "#1a1a2a",
          borderRadius: "10px",
          padding: "20px",
          textAlign: "center",
          color: "#666"
        }}>
          <div style={{ fontSize: "2rem", marginBottom: "10px" }}>🎨</div>
          <p style={{ margin: 0, fontSize: "0.85rem" }}>
            Visual diagrams will appear here as you progress through the lesson
          </p>
        </div>
      )}
      
      {/* Full-size diagram modal */}
      {selectedDiagram && (
        <div
          onClick={() => setSelectedDiagram(null)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.9)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10001,
            cursor: "pointer"
          }}
        >
          <div style={{ maxWidth: "90%", maxHeight: "90%" }}>
            <img
              src={selectedDiagram.url}
              alt={selectedDiagram.topic}
              style={{
                maxWidth: "100%",
                maxHeight: "80vh",
                borderRadius: "10px"
              }}
            />
            <p style={{
              textAlign: "center",
              color: "#fff",
              marginTop: "15px",
              fontSize: "1rem"
            }}>
              {selectedDiagram.description || selectedDiagram.topic}
            </p>
            <p style={{
              textAlign: "center",
              color: "#666",
              fontSize: "0.8rem"
            }}>
              Click anywhere to close
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default VisualContextEngine;
